import type { ReactNode, InputHTMLAttributes, SelectHTMLAttributes } from 'react';
import type { Tone } from '../lib/format';

export function Pill({ tone, label }: { tone: Tone; label: string }) {
  return <span className={`pill ${tone}`}>{label}</span>;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="skeleton">
      <span className="spinner" aria-hidden="true" />
      {label ? <span>{label}</span> : null}
    </span>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="notice bad" role="alert">
      <span style={{ flex: 1 }}>{message}</span>
      {onRetry ? <button className="btn" onClick={onRetry}>重试</button> : null}
    </div>
  );
}

export function SearchField({ value, onChange, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="search-field">
      <svg className="mag" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" strokeLinecap="round" />
      </svg>
      <input
        className="txt"
        type="search"
        value={value}
        placeholder={placeholder || '搜索编号 / 名称 / 税号…'}
        onChange={(e) => onChange(e.target.value)}
        aria-label="关键字搜索"
      />
    </div>
  );
}

export function YearField({ value, onChange, years }: {
  value: string;
  onChange: (v: string) => void;
  years: string[];
}) {
  return (
    <label className="field">
      <label>年度</label>
      <select className="sel" value={value} onChange={(e) => onChange(e.target.value)} aria-label="年度筛选">
        <option value="">全部年度</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </label>
  );
}

export function KeyValue({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className="v">{children ?? '—'}</span>
    </div>
  );
}

// 明细表（报价/合同/交货详情里的行项目）。
export function LineTable<R>({ columns, rows, emptyText = '暂无明细' }: {
  columns: { header: string; align?: 'left' | 'right'; render: (row: R) => ReactNode }[];
  rows: R[];
  emptyText?: string;
}) {
  if (!rows || !rows.length) return <div className="muted" style={{ fontSize: 12.5 }}>{emptyText}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="lines">
        <thead>
          <tr>
            {columns.map((c) => <th key={c.header} style={{ textAlign: c.align || 'left' }}>{c.header}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => <td key={c.header} style={{ textAlign: c.align || 'left' }}>{c.render(row)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- 表单基元（报价 / 合同 / 交货编辑骨架共用）----
export function FormGrid({ children, min = 200 }: { children: ReactNode; min?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill,minmax(${min}px,1fr))`, gap: 12 }}>
      {children}
    </div>
  );
}

export function Field({ label, hint, error, required, children, span }: {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  span?: boolean;
}) {
  return (
    <label className="field" style={span ? { gridColumn: '1 / -1' } : undefined}>
      <label>{label}{required ? ' *' : ''}</label>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-hint bad">{error}</span> : null}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="txt" {...props} />;
}
export function NumberInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="txt num-input" inputMode="decimal" type="number" {...props} />;
}
export function DateInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="txt" type="date" {...props} />;
}
export function TextArea(props: InputHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="txt area" rows={2} {...props} />;
}
export function SelectInput({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="sel" {...props}>{children}</select>;
}
